import { useEffect, useRef, type ReactNode } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface MagneticProps {
  readonly children: ReactNode;
  /** How far the element is pulled toward the pointer, as a fraction of offset. */
  readonly strength?: number;
  /** Pointer distance (px) at which the pull starts. */
  readonly radius?: number;
  readonly className?: string;
}

/**
 * Pulls its child toward the pointer as the pointer approaches, springing
 * back on leave. Pure craft — it communicates nothing, which is an acceptable
 * reason for a micro-interaction but not for a major system
 * (docs/DESIGN-V7.md §10).
 *
 * Listens on the element rather than the window: a document-level pointermove
 * multiplied across every magnetic element on the page is real main-thread
 * cost for an effect that only matters within a few dozen pixels.
 *
 * Renders a plain wrapper with no transform at all when gated off, so touch
 * and reduced-motion visitors get an ordinary, correctly-positioned element.
 */
export function Magnetic({ children, strength = 0.35, radius = 90, className }: MagneticProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const finePointer = useMediaQuery('(pointer: fine)');
  const reduced = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el || !finePointer || reduced) return;

    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const dx = e.clientX - (rect.left + rect.width / 2);
      const dy = e.clientY - (rect.top + rect.height / 2);
      const dist = Math.hypot(dx, dy);
      const falloff = Math.max(0, 1 - dist / (radius + Math.max(rect.width, rect.height) / 2));
      el.style.transform = `translate3d(${dx * strength * falloff}px, ${dy * strength * falloff}px, 0)`;
    };

    const onLeave = () => {
      el.style.transform = '';
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      el.style.transform = '';
    };
  }, [finePointer, reduced, strength, radius]);

  return (
    <span ref={ref} className={`ease-settle inline-block transition-transform duration-300 ${className ?? ''}`}>
      {children}
    </span>
  );
}
